<template>
  <div v-for="(group, key) in groupedPages" :key="key" class="mb-4 px-2">
    <div v-if="group.type && !isCollapsed" class="group-label">
      {{ $t(group.type) }}
    </div>
    <ul class="flex flex-col gap-1" :class="isCollapsed ? '' : ''">
      <li v-for="(button, index) in group.items" :key="index" class="nav">
        <!-- Expandable parent (only when sidebar is full) -->
        <template v-if="hasChildren(button) && !isCollapsed">
          <CollapsibleRoot v-model:open="openMap[button.name]" as-child>
            <div>
              <CollapsibleTrigger as-child>
                <button
                  type="button"
                  class="nav-link full nav-link--parent"
                  :class="{
                    'is-active-parent': isAnyChildActive(button),
                  }"
                  @click="onParentClick(button)"
                >
                  <div class="flex items-center gap-2 min-w-0 flex-1">
                    <svg-icon
                      v-if="button.icon"
                      :name="button.icon"
                      class="shrink-0"
                    />
                    <span class="nav-label truncate">{{ button.title }}</span>
                  </div>
                  <span v-if="openMap[button.name]" class="nav-chevron">−</span>
                  <span v-else class="nav-chevron">+</span>
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <ul class="nav-sub">
                  <li v-for="child in button.children" :key="child.name">
                    <router-link
                      :to="{ name: child.name }"
                      class="nav-link nav-link--sub"
                      @click="$emit('setActiveItemHandler', child.name)"
                    >
                      <div class="text-[13px] truncate">{{ child.title }}</div>
                    </router-link>
                  </li>
                </ul>
              </CollapsibleContent>
            </div>
          </CollapsibleRoot>
        </template>

        <!-- Flat item or collapsed-sidebar fallback -->
        <el-tooltip
          v-else
          :content="button.title"
          placement="right"
          :disabled="!isCollapsed"
          :showAfter="300"
        >
          <router-link
            v-if="button.name"
            :to="{ name: button.name }"
            class="nav-link"
            :class="[
              isCollapsed ? 'short' : 'full',
              isCollapsed ? 'nav-link--collapsed' : '',
              getClassName(button.name),
            ]"
            @click="$emit('setActiveItemHandler', button.name)"
          >
            <div class="flex items-center gap-2">
              <svg-icon
                v-if="button.icon"
                :name="button.icon"
                class="shrink-0"
              />

              <span v-if="!isCollapsed" class="nav-label">
                {{ button.title }}
              </span>
            </div>
            <span v-if="!isCollapsed && button.badge" class="notif"
              >{{ button.badge }}
            </span>
          </router-link>
        </el-tooltip>
      </li>
    </ul>
  </div>
</template>

<script>
import {
  CollapsibleRoot,
  CollapsibleTrigger,
  CollapsibleContent,
} from "reka-ui";
export default {
  name: "NavBar",
  components: {
    CollapsibleRoot,
    CollapsibleTrigger,
    CollapsibleContent,
  },
  props: {
    isCollapsed: {
      type: Boolean,
    },
    activeItem: {
      type: String,
    },
    pages: {
      type: Array,
    },
  },
  emits: ["setActiveItemHandler"],
  data() {
    return {
      openMap: {},
    };
  },
  watch: {
    "$route.name": {
      immediate: true,
      handler() {
        this.syncOpen();
      },
    },
    pages: {
      immediate: true,
      deep: true,
      handler() {
        this.syncOpen();
      },
    },
  },
  methods: {
    hasChildren(button) {
      return Array.isArray(button.children) && button.children.length > 0;
    },
    isAnyChildActive(button) {
      return (button.children || []).some((c) => c.name === this.$route.name);
    },
    syncOpen() {
      for (const button of this.pages || []) {
        if (this.hasChildren(button) && this.isAnyChildActive(button)) {
          this.openMap[button.name] = true;
        }
      }
    },
    onParentClick(button) {
      // если ни один child этой группы ещё не активен — навигируем на parent
      // route, который через redirect выберет первого ребёнка автоматически.
      // если один из children уже активен — просто оставляем CollapsibleTrigger
      // тогглить раскрытие, не теряя текущий выбор.
      if (this.isAnyChildActive(button)) return;
      this.$emit("setActiveItemHandler", button.name);
      this.$router.push({ name: button.name }).catch(() => {});
    },
    getClassName(name) {
      if (name === "ai-assistant") {
        return "ai";
      }
      if (name === "calendar_events") {
        return "calendar";
      }
      if (name === "meet") {
        return "meet";
      }
      if (name === "todo") {
        return "todo";
      }
      return "";
    },
  },
  computed: {
    groupedPages() {
      const grouped = this.pages.reduce((acc, item) => {
        const { type } = item;
        if (!acc[type]) {
          acc[type] = { type, items: [] };
        }
        acc[type].items.push(item);
        return acc;
      }, {});

      return Object.values(grouped);
    },
  },
};
</script>

<style lang="scss" scoped>
.nav-link {
  @apply flex items-center justify-between rounded-md px-2 py-1.5 gap-2 text-left text-sm border border-transparent;

  &.short {
    @apply justify-center px-0;
  }
  color: hsl(var(--sidebar-foreground));
  &:hover {
    @apply border border-border;
    background-color: rgb(var(--color-white));
    color: hsl(var(--sidebar-accent-foreground));

    svg {
      color: hsl(var(--sidebar-accent-foreground));
    }
  }

  svg {
    @apply flex justify-center  shrink-0;
    color: hsl(var(--sidebar-foreground));
    width: 18px;
    height: 18px;
  }

  &.ai {
    svg {
      color: rgb(var(--color-purple-700));
      fill: rgb(var(--color-purple-50));
    }
  }

  &.calendar {
    svg {
      color: rgb(var(--color-blue-400));
      fill: rgb(var(--color-blue-50));
    }
  }

  &.meet {
    svg {
      color: rgb(var(--color-red-600));
      fill: rgb(var(--color-red-300));
    }
  }

  &.todo {
    svg {
      color: rgb(var(--color-green-700));
      fill: rgb(var(--color-green-100));
    }
  }
}

.nav-link--parent {
  @apply w-full;
  background: transparent;
  cursor: pointer;
  outline: none;

  &.is-active-parent {
    @apply border border-border;
    background-color: rgb(var(--color-white));
    color: rgb(var(--color-blue-500));

    svg {
      color: rgb(var(--color-blue-500));
    }
  }
}

.nav-link--sub {
  @apply px-2 py-1 justify-start;
  position: relative;

  // короткий горизонтальный «усик» к вертикальной линии слева
  &::before {
    content: "";
    position: absolute;
    left: -12px; // совпадает с pl-3 у .nav-sub
    top: 50%;
    width: 8px;
    border-top: 1px solid hsl(var(--sidebar-border));
    pointer-events: none;
  }

  &:hover {
    background: transparent;
    border-color: transparent;
    color: rgb(var(--color-blue-500));

    svg {
      color: rgb(var(--color-blue-500));
    }
  }

  // активный child — только синий цвет, без бордера и фона
  &.router-link-active,
  &.router-link-exact-active {
    background: transparent;
    border-color: transparent;
    color: rgb(var(--color-blue-500));

    &:hover {
      background: transparent;
      border-color: transparent;
    }
  }
}

.nav-sub {
  @apply flex flex-col mt-1 pl-3;
  // 17px = .nav-link px-2 (8px) + svg width / 2 (9px) — выравнивает линию
  // ровно по центру иконки родителя
  margin-left: 17px;

  > li {
    position: relative;

    // вертикальная линия — кусочек на каждый li, чтобы у последнего
    // её можно было обрезать до центра (где входит «усик»)
    &::before {
      content: "";
      position: absolute;
      left: -12px;
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: hsl(var(--sidebar-border));
      pointer-events: none;
    }

    // последний li — линия только до центра элемента
    &:last-child::before {
      bottom: 50%;
    }
  }
}

.nav-chevron {
  @apply shrink-0 inline-flex items-center justify-center;
  width: 16px;
  height: 16px;
  font-size: 15px;
  font-weight: 400;
  line-height: 1;
  color: hsl(var(--sidebar-foreground) / 0.6);
}

.nav-label {
  @apply text-sm;
}

a.router-link-active {
  @apply border border-border;
  background-color: rgb(var(--color-white));
  color: rgb(var(--color-blue-500));

  svg {
    color: hsl(var(--sidebar-accent-foreground));
    color: rgb(var(--color-blue-500));
    //width: 14px;
    //height: 14px;
  }

  &.nav-link--collapsed {
    background-color: hsl(var(--foreground));
    color: hsl(var(--background));

    svg {
      color: hsl(var(--background));
    }
  }
}

.group-label {
  @apply font-medium uppercase tracking-wider px-2 py-1.5;
  color: hsl(var(--sidebar-foreground) / 0.5);
  font-size: 11px;
}

.notif {
  @apply w-5 h-5 flex items-center justify-center rounded-md text-xs;
  background-color: hsl(var(--sidebar-accent));
  color: hsl(var(--sidebar-accent-foreground));
}
</style>
